import {
  Fragment,
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { CheckCircle2, Trash2 } from 'lucide-react';
import { FINDING, type DocumentResult, type LineItemRow } from '../shared/desktop-api';
import { findingStillHolds } from '../shared/finding-clearing';
import type { LineItemCell } from '../shared/line-item-evidence';
import {
  arithmeticValidatedAfterEdit,
  lineItemArithmetic,
  lineItemBalances,
  lineItemSum,
  money,
} from '../shared/line-item-arithmetic';
import { FieldDraftStore, type FieldDraft } from './field-drafts';
import {
  createLineDraftContext,
  lineDraftBinding,
  lineDraftCells,
  lineDraftMatchesRow,
  lineDraftTarget,
  readLineDraftValue,
  type LineDraftCell,
  type LineDraftContext,
} from './line-item-drafts';
import { LineItemCheck } from './line-item-check';
import { RowSourceCrop } from './RowSourceCrop';
import { LineItemToolbar, type LineItemToolbarAction } from './LineItemToolbar';
import './line-item-ledger.css';
import { TooltipButton } from './TooltipButton';
import { noteTextBoxBaseline } from './text-undo';

/**
 * All document changes still go through the authoritative host's save lane. There is no row
 * confirm (single confirm, October 5, 2026): the table is confirmed with its document.
 * `onAcceptRow` is "Looks right" on a line that doesn't add up, which answers that question.
 */
export type LineItemLedgerActions = {
  onEnterRow: (row: LineItemRow, cell: LineItemCell) => void;
  onAcceptRow: (row: LineItemRow) => void;
  onUseSum: (row: LineItemRow, amount: number) => void;
  onDeleteRow: (rowNumber: number) => void;
};

/** Presentation only: preserve every digit of the stored numeric spelling,
 * including small scientific-notation unit prices. Do not round the value or
 * infer a currency; the editable binding and native correction stay raw. */
function formatLineCellDisplay(cell: LineDraftCell, value: string, grouped = true): string {
  if (cell !== 'unitPrice' && cell !== 'amount') return value;
  const parts = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i.exec(value);
  if (!parts || !Number.isFinite(Number(value))) return value;
  const [, sign, whole, fraction = '', exponent = '0'] = parts;
  const digits = whole + fraction;
  const point = whole.length + Number(exponent);
  const integer = point <= 0 ? '0' : digits.slice(0, point).padEnd(point, '0');
  const decimals = point <= 0 ? '0'.repeat(-point) + digits : digits.slice(point);
  return `${sign}${grouped ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : integer}.${decimals.padEnd(2, '0')}`;
}

function LineCell({
  document,
  row,
  cell,
  store,
  context,
  onEnter,
  hint,
  hintId,
  suspendBlurCommit = false,
}: {
  document: DocumentResult;
  row: LineItemRow;
  cell: LineDraftCell;
  store: FieldDraftStore;
  context: LineDraftContext;
  onEnter: () => void;
  hint?: ReactNode;
  hintId?: string;
  suspendBlurCommit?: boolean;
}) {
  const binding = lineDraftBinding(document, row, cell, context);
  const draft = store.get(binding);
  const sameRow = !draft || lineDraftMatchesRow(draft, document, context);
  const [error, setError] = useState<string>();
  const [focused, setFocused] = useState(false);
  const rawValue = sameRow ? (draft?.value ?? binding.baseValue) : binding.baseValue;
  const displayedValue =
    sameRow && draft ? rawValue : formatLineCellDisplay(cell, binding.baseValue, !focused);
  return (
    <td className={cell === 'amount' ? 'line-amount-cell' : undefined}>
      <input
        className={cell === 'description' ? undefined : 'number-cell'}
        data-line-cell={cell}
        inputMode={cell === 'description' ? undefined : 'decimal'}
        aria-label={binding.name}
        aria-describedby={hintId}
        // A cell repeats its value on hover only when the column cuts it off (a long description,
        // or a very precise number in a narrow column); otherwise the value is already on screen.
        onMouseEnter={(event) => {
          const input = event.currentTarget;
          if (input.scrollWidth > input.clientWidth) input.title = input.value;
          else input.removeAttribute('title');
        }}
        value={displayedValue}
        disabled={!sameRow || draft?.state === 'saving'}
        // Remove grouping but retain decimal precision before selection. A deferred
        // controlled-value update can otherwise overwrite that selection and
        // append the first typing to the original monetary value.
        onFocus={(event) => {
          const input = event.currentTarget;
          const selectedAll =
            input.selectionStart === 0 && input.selectionEnd === input.value.length;
          flushSync(() => setFocused(true));
          noteTextBoxBaseline(input);
          if (selectedAll || (!draft && (cell === 'unitPrice' || cell === 'amount')))
            input.select();
          onEnter();
        }}
        // Tab may arrive before the preceding save settles. First typing uses
        // the displayed binding; the store preserves an existing draft's binding.
        onChange={(event) => setError(store.edit(binding, event.target.value))}
        onKeyDown={(event) => {
          // Escape puts the cell back to its saved value.
          if (event.key === 'Escape' && draft && draft.state !== 'saving') {
            event.preventDefault();
            event.stopPropagation();
            store.discard(draft);
            return;
          }
          // Enter saves this cell only and moves down to the same column (Shift+Enter: up), like a
          // spreadsheet. It never confirms the row or the document; Ctrl+Enter stays the document's.
          // The down and up arrows do the same (shortcuts audit, 2026-10-07): the spreadsheet habit.
          if (event.ctrlKey || event.metaKey || event.altKey) return;
          const arrow = (event.key === 'ArrowDown' || event.key === 'ArrowUp') && !event.shiftKey;
          if (event.key !== 'Enter' && !arrow) return;
          const up = event.key === 'ArrowUp' || (event.key === 'Enter' && event.shiftKey);
          event.preventDefault();
          if (draft?.state === 'editing' && sameRow) store.submit(draft);
          const selector = `input[data-line-cell="${cell}"]`;
          let sibling = up
            ? event.currentTarget.closest('tr')?.previousElementSibling
            : event.currentTarget.closest('tr')?.nextElementSibling;
          while (sibling && !sibling.querySelector(selector))
            sibling = up ? sibling.previousElementSibling : sibling.nextElementSibling;
          sibling?.querySelector<HTMLInputElement>(selector)?.focus();
        }}
        onBlur={(event) => {
          setFocused(false);
          // A presentation change may hide this editor; keep its raw draft,
          // rather than treating the resulting focus loss as a save command.
          if (sameRow && !suspendBlurCommit && event.currentTarget.getClientRects().length > 0)
            store.submit(binding);
        }}
      />
      {hint}
      {error && (
        <small className="line-draft-error" role="alert" data-telemetry="error:field-edit-refused">
          {error.replace('unsaved field', 'unsaved value').replace('This field', 'This value')}
        </small>
      )}
    </td>
  );
}

function LineDraftConflict({
  draft,
  document,
  store,
  context,
  disabled,
}: {
  draft: FieldDraft;
  document: DocumentResult;
  store: FieldDraftStore;
  context: LineDraftContext;
  disabled: boolean;
}) {
  const target = lineDraftTarget(draft);
  const row = target && document.lineItems.find((row) => row.rowNumber === target.rowNumber);
  const sameRow = lineDraftMatchesRow(draft, document, context);
  const binding = row && target ? lineDraftBinding(document, row, target.cell, context) : null;
  return (
    <div className="line-draft-conflict" role="status">
      <strong>{draft.name} · not saved</strong>
      <span>
        {sameRow
          ? (draft.reason ?? 'This document changed while you were editing.')
          : 'The original row cannot be identified safely. Copy or discard this typing.'}
      </span>
      <span>
        Your typing: <code>{draft.value || '(blank)'}</code>
      </span>
      {sameRow && binding && (
        <span>
          Current value: <code>{binding.baseValue || '(blank)'}</code>
        </span>
      )}
      {sameRow && binding && (
        <button
          className="line-draft-choice"
          type="button"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => store.replace(binding)}
        >
          Save my value
        </button>
      )}
      <button
        className="line-draft-choice"
        type="button"
        disabled={draft.state === 'saving'}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => store.discard(draft)}
      >
        {sameRow ? 'Use current value' : 'Discard unsaved typing'}
      </button>
    </div>
  );
}

/** Unavailable rows/documents must not occupy hidden draft slots forever. This
 * disclosure also remains reachable when the current document has no table. */
export function RetainedLineDrafts({
  store,
  document,
  availableDocumentIds,
}: {
  store: FieldDraftStore;
  document?: DocumentResult;
  availableDocumentIds: readonly string[];
}) {
  const drafts = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const unavailable = drafts.filter(
    (draft) =>
      !availableDocumentIds.includes(draft.documentId) ||
      (document &&
        draft.documentId === document.id &&
        (!lineDraftTarget(draft) ||
          !document.lineItems.some((row) => row.rowNumber === lineDraftTarget(draft)?.rowNumber))),
  );
  if (!unavailable.length) return null;
  return (
    <details className="retained-field-drafts retained-line-drafts">
      <summary>Unsaved line-item changes · {unavailable.length}</summary>
      {unavailable.map((draft) => (
        <div className="line-draft-conflict" key={draft.id}>
          <strong>
            {draft.name} · {document ? 'original row unavailable' : 'document not open'}
          </strong>
          <code>{draft.value || '(blank)'}</code>
          <button
            type="button"
            disabled={draft.state === 'saving'}
            onClick={() => store.discard(draft)}
          >
            Discard unsaved typing
          </button>
        </div>
      ))}
    </details>
  );
}

/** One editor in docked and detached surfaces. Raw drafts live in the shared
 * owner-backed store, not a disappearing row. Blur submits; only a correlated
 * native acknowledgement clears typing. Unmount is never a save gesture. */
export function LineItemLedger({
  document,
  draftStore,
  activeRow,
  arrivedRow,
  arrivalRevision,
  flashRow,
  saving,
  confirmationDisabled = saving,
  confirmingRows,
  actions,
  compareSources = false,
  onShowSource,
  toolbarStart,
  toolbarStatus,
  toolbarActions,
  onCollapse,
  collapseLabel,
  suspendBlurCommit = false,
}: {
  document: DocumentResult;
  draftStore: FieldDraftStore;
  activeRow?: number | null;
  arrivedRow?: number | null;
  arrivalRevision?: number;
  /** A line the main button refused to confirm over ("Check line 2's amount first"): it flashes, each new serial once. */
  flashRow?: { rowNumber: number; serial: number } | null;
  saving: boolean;
  actions: LineItemLedgerActions;
  confirmationDisabled?: boolean;
  confirmingRows?: ReadonlySet<number>;
  compareSources?: boolean;
  onShowSource?: (row: LineItemRow, pageNumber: number) => void;
  /** Presentation slots only; each surface retains its existing action owner. */
  toolbarStart?: ReactNode;
  toolbarStatus?: ReactNode;
  toolbarActions?: readonly LineItemToolbarAction[];
  onCollapse?: () => void;
  collapseLabel?: string;
  suspendBlurCommit?: boolean;
}) {
  const drafts = useSyncExternalStore(draftStore.subscribe, draftStore.getSnapshot).filter(
    (draft) => draft.documentId === document.id,
  );
  const rows = document.lineItems;
  // The same retained reading finding used by the review checklist. Arithmetic
  // agreement alone is not evidence that the printed cells were read correctly.
  const readingNeedsReview = (document.validationFindings ?? []).some(
    (finding) =>
      finding.code === FINDING.lineItemReadIssue &&
      (finding.severity === 'warning' || finding.severity === 'error') &&
      findingStillHolds(finding, document),
  );
  const context = createLineDraftContext(document);
  const ledgerId = useId();
  const tableRef = useRef<HTMLTableElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const hasToolbar =
    rows.length > 0 ||
    Boolean(toolbarStart || toolbarStatus || toolbarActions?.length || onCollapse);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const hasIssue = (row: LineItemRow) =>
    row.quantity != null &&
    row.unitPrice != null &&
    row.amount != null &&
    !lineItemBalances(row) &&
    !row.arithmeticValidated;
  const issueCount = rows.filter(hasIssue).length;
  const visibleRows = rows.filter(
    (row) =>
      !issuesOnly ||
      hasIssue(row) ||
      drafts.some((draft) => lineDraftTarget(draft)?.rowNumber === row.rowNumber),
  );
  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    const table = tableRef.current;
    if (!toolbar || !table) return;
    // A horizontal scrollbar or zoom can change the toolbar's height. Keep the
    // column headings below the actual bar, not a guessed desktop pixel size.
    const measure = () =>
      table.style.setProperty(
        '--line-toolbar-height',
        `${toolbar.getBoundingClientRect().height}px`,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [hasToolbar, document.id]);
  useLayoutEffect(() => {
    if (arrivedRow == null) return;
    // A new explicit evidence arrival must reveal its editor even when an
    // arithmetic-only filter hid this balanced row. Ordinary filter changes do
    // not replay an old arrival or redefine which rows have arithmetic issues.
    if (
      issuesOnly &&
      rows.some((row) => row.rowNumber === arrivedRow) &&
      !visibleRows.some((row) => row.rowNumber === arrivedRow)
    )
      setIssuesOnly(false);
    const frame = requestAnimationFrame(() => {
      const table = tableRef.current;
      const row = table?.querySelector<HTMLElement>(`tr[data-line-row="${arrivedRow}"]`);
      if (!table || !row) return;
      // Upward arrival must clear the sticky header. Measure its current size
      // so a larger font or resized window cannot cover the destination editor.
      row.style.scrollMarginTop = `${
        (table.tHead?.getBoundingClientRect().height ?? 0) +
        (toolbarRef.current?.getBoundingClientRect().height ?? 0)
      }px`;
      row.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [arrivedRow, arrivalRevision]);
  useLayoutEffect(() => {
    if (!flashRow) return;
    if (issuesOnly && !visibleRows.some((row) => row.rowNumber === flashRow.rowNumber))
      setIssuesOnly(false);
    const frame = requestAnimationFrame(() => {
      const row = tableRef.current?.querySelector<HTMLElement>(
        `tr[data-line-row="${flashRow.rowNumber}"]`,
      );
      if (!row) return;
      row.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      // Its question open, so "Looks right" and the sum are right there to answer it.
      const question = row.querySelector<HTMLDetailsElement>('details.line-check');
      if (question) question.open = true;
      // Twice, softly, the line the main button is waiting on. Reduced motion keeps the line's
      // own highlight and the words in the bar.
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      for (const cell of row.querySelectorAll<HTMLElement>(':scope > td'))
        cell.animate?.(
          [
            { backgroundColor: 'transparent' },
            { backgroundColor: 'var(--cp-warning-soft)' },
            { backgroundColor: 'transparent' },
          ],
          { duration: 900, iterations: 2, easing: 'ease-in-out' },
        );
    });
    return () => cancelAnimationFrame(frame);
  }, [flashRow?.serial]);

  const edited = (row: LineItemRow) => {
    const shown = { ...row };
    for (const cell of lineDraftCells) {
      const draft = draftStore.get(lineDraftBinding(document, row, cell, context));
      if (!draft || !lineDraftMatchesRow(draft, document, context)) continue;
      const value = readLineDraftValue(cell, draft.value);
      if (value !== undefined) Object.assign(shown, { [cell]: value });
    }
    return { ...shown, arithmeticValidated: arithmeticValidatedAfterEdit(row, shown) };
  };
  return (
    <>
      {hasToolbar && (
        <LineItemToolbar
          elementRef={toolbarRef}
          owner={`${document.id}:${context.documentMutationToken}`}
          total={rows.length}
          issueCount={issueCount}
          issuesOnly={issuesOnly}
          onFilter={setIssuesOnly}
          title={toolbarStart}
          status={toolbarStatus}
          actions={toolbarActions}
          onCollapse={onCollapse}
          collapseLabel={collapseLabel}
        />
      )}
      {issuesOnly && visibleRows.length === 0 && (
        <p className="line-issues-empty">
          <CheckCircle2 size={18} />
          Every line adds up
        </p>
      )}
      <table
        key={document.id}
        className={`line-item-review-table${hasToolbar ? ' line-toolbar-table' : ''}${compareSources ? ' source-comparison-table' : ''}`}
        ref={tableRef}
      >
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">Row</span>
            </th>
            <th scope="col">Description</th>
            <th scope="col">Qty</th>
            <th scope="col">Unit price</th>
            <th scope="col">Amount</th>
            <th scope="col" className="line-row-tools">
              <span className="visually-hidden">Delete</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const shown = edited(row);
            const rowDrafts = drafts.filter(
              (draft) => lineDraftTarget(draft)?.rowNumber === row.rowNumber,
            );
            const conflicts = rowDrafts.filter(
              (draft) =>
                draft.state !== 'saving' &&
                (draft.state === 'refused' ||
                  draft.documentMutationToken !== context.documentMutationToken),
            );
            const settled = lineItemBalances(shown) || shown.arithmeticValidated;
            const arithmetic = lineItemArithmetic(shown);
            const matches = arithmetic.verdict === 'balanced' || arithmetic.verdict === 'credit';
            const checkable =
              shown.quantity != null && shown.unitPrice != null && shown.amount != null;
            const needsReadingCheck = readingNeedsReview && !row.verified;
            const needsAttention = needsReadingCheck || (!settled && checkable);
            const arithmeticHintId = `${ledgerId}-amount-${row.rowNumber}`;
            const pairClass = compareSources
              ? ` line-source-pair ${rowIndex % 2 ? 'line-source-pair-alternate' : ''}`
              : '';
            return (
              <Fragment key={row.rowNumber}>
                {compareSources && visibleRows.includes(row) && (
                  <tr
                    key="source"
                    data-source-pair={row.rowNumber}
                    className={`line-source-comparison${pairClass}`}
                  >
                    <td colSpan={6}>
                      <RowSourceCrop
                        document={document}
                        row={row}
                        documentMutationToken={context.documentMutationToken}
                        tableIdentity={context.tableIdentity}
                        context={context}
                        onShowPage={
                          onShowSource ? (pageNumber) => onShowSource(row, pageNumber) : undefined
                        }
                      />
                    </td>
                  </tr>
                )}
                <tr
                  key="values"
                  title={
                    matches
                      ? `Amount matches: ${lineItemSum(shown.quantity!, shown.unitPrice!, arithmetic.discountPercent)} = ${money(arithmetic.expected)}${arithmetic.verdict === 'credit' ? ' (credit)' : ''}. Arithmetic check only.`
                      : undefined
                  }
                  data-line-row={row.rowNumber}
                  data-source-pair={compareSources ? row.rowNumber : undefined}
                  hidden={!visibleRows.includes(row)}
                  className={`${arrivedRow === row.rowNumber ? 'review-arrived' : ''} ${matches ? 'arithmetic-matches-row' : ''} ${needsAttention ? 'review-row' : 'balanced-row'} ${activeRow === row.rowNumber ? 'active-evidence-row' : ''}${pairClass}`}
                >
                  <td>
                    <span className="line-row-number">{row.rowNumber}</span>
                    {needsReadingCheck && (
                      <span className="visually-hidden">Check this row against the page</span>
                    )}
                  </td>
                  {lineDraftCells.map((cell) => (
                    <LineCell
                      key={cell}
                      document={document}
                      row={row}
                      cell={cell}
                      store={draftStore}
                      context={context}
                      suspendBlurCommit={suspendBlurCommit}
                      onEnter={() => actions.onEnterRow(row, cell)}
                      hintId={cell === 'amount' ? arithmeticHintId : undefined}
                      hint={
                        cell === 'amount' ? (
                          <LineItemCheck
                            id={arithmeticHintId}
                            item={shown}
                            accepting={
                              confirmationDisabled ||
                              confirmingRows?.has(row.rowNumber) ||
                              rowDrafts.length > 0
                            }
                            onAccept={() => actions.onAcceptRow(row)}
                            onUseSum={(value) =>
                              actions.onUseSum(row, Math.round(value * 100) / 100)
                            }
                          />
                        ) : undefined
                      }
                    />
                  ))}
                  <td className="line-row-tools">
                    <TooltipButton
                      type="button"
                      className="line-row-delete"
                      onClick={() => actions.onDeleteRow(row.rowNumber)}
                      disabled={saving}
                      tooltip="Delete row"
                      aria-label={`Delete line ${row.rowNumber}`}
                    >
                      <Trash2 size={16} />
                    </TooltipButton>
                  </td>
                </tr>
                {conflicts.length > 0 && (
                  <tr key="conflict" className="line-draft-conflict-row">
                    <td colSpan={6}>
                      {conflicts.map((draft) => (
                        <LineDraftConflict
                          key={draft.id}
                          draft={draft}
                          document={document}
                          store={draftStore}
                          context={context}
                          disabled={saving}
                        />
                      ))}
                    </td>
                  </tr>
                )}
                {compareSources && visibleRows.includes(row) && (
                  <tr
                    key="pair-gap"
                    className="line-source-pair-gap"
                    data-source-gap={row.rowNumber}
                    aria-hidden="true"
                  >
                    <td colSpan={6} />
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
